/**
 * C2 — "Disclosure agreements".
 *
 * A disclosure agreement is a ladder with six rungs, and the counterparty stays
 * ANONYMOUS until rung 4, after a mutual NDA. That is the point of the ladder:
 * two parties find out whether they are worth talking to before either has to
 * say who they are. So this card prints the rung, and prints a name only when
 * the agreement itself has released one — an anonymous counterparty reads
 * "Anonymous organisation", never a guess.
 *
 * "Hold at this level" is the real control the figure shows: it declines a
 * pending advance the counterparty has consented to and the caller has not. It
 * is offered only when there IS such an advance — a button that would do nothing
 * is worse than no button, because it implies a state that is not there.
 */
import { useState } from 'react'

export interface DisclosureRow {
  agreementId: string
  projectName: string
  /** The counterparty's name once the ladder has released it, else null. */
  counterparty: string | null
  currentLevel: number
  /** True when the other side has consented above the caller's own level. */
  advancePending: boolean
}

export interface DisclosureAgreementsPanelProps {
  agreements: DisclosureRow[]
  loaded: boolean
  error?: boolean
  onHold: (agreementId: string) => Promise<void>
}

const RUNGS = 6

function Rungs({ level }: { level: number }) {
  return (
    <span className="inline-flex items-center gap-[3px]" title={`Rung ${level} of ${RUNGS}`}>
      {Array.from({ length: RUNGS }, (_, i) => (
        <i
          key={i}
          aria-hidden
          className={`inline-block h-[5px] w-[14px] rounded-full ${i < level ? 'bg-iflow-500' : 'bg-slate-200'}`}
        />
      ))}
    </span>
  )
}

function Row({ row, onHold }: { row: DisclosureRow; onHold: (id: string) => Promise<void> }) {
  const [busy, setBusy] = useState(false)
  const [errorMessage, setErrorMessage] = useState<string | null>(null)

  const hold = async () => {
    setBusy(true)
    setErrorMessage(null)
    try {
      await onHold(row.agreementId)
    } catch (err) {
      const message =
        (err as { response?: { data?: { error?: string } } })?.response?.data?.error
        ?? 'Could not hold this agreement at its current level.'
      setErrorMessage(message)
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="border-t border-slate-100 py-2 first:border-t-0">
      <div className="flex items-start gap-2.5 text-[12.5px]">
        <span className="min-w-0 flex-1 text-slate-600">
          <b className="text-slate-900">{row.projectName}</b> ·{' '}
          {row.counterparty ?? 'Anonymous organisation'}
          <span className="mt-1 flex items-center gap-2 text-[11.5px] text-slate-500">
            <Rungs level={row.currentLevel} /> rung {row.currentLevel} of {RUNGS}
          </span>
        </span>
        {row.advancePending && (
          <button
            type="button"
            onClick={hold}
            disabled={busy}
            className="shrink-0 whitespace-nowrap rounded border border-slate-300 px-2.5 py-1 text-[11.5px] font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-50"
          >
            {busy ? 'Holding…' : 'Hold at this level'}
          </button>
        )}
      </div>
      {errorMessage && <p className="mt-1 text-[11.5px] text-rose-600">{errorMessage}</p>}
    </div>
  )
}

export default function DisclosureAgreementsPanel({
  agreements, loaded, error, onHold,
}: DisclosureAgreementsPanelProps) {
  return (
    <div data-part="DisclosureAgreementsPanel" className="rounded-xl border border-slate-200 bg-white px-4 py-3">
      <h2 className="mb-1 flex items-center gap-2 text-[14px] font-bold text-slate-900">
        Disclosure agreements
        <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[11px] font-bold text-slate-500">
          {loaded && !error ? agreements.length : '—'}
        </span>
      </h2>

      {!loaded && !error && <p className="text-[12.5px] text-slate-400">Loading…</p>}
      {error && (
        <p className="text-[12.5px] text-rose-600">
          Could not load your disclosure agreements.
        </p>
      )}
      {loaded && !error && agreements.length === 0 && (
        <p className="text-[12.5px] text-slate-600">
          No disclosure agreement is open on any of your projects.
        </p>
      )}
      {loaded && !error && agreements.map((row) => (
        <Row key={row.agreementId} row={row} onHold={onHold} />
      ))}

      <p className="mt-2 text-[11.5px] leading-relaxed text-slate-500">
        Identities appear only from rung 4, after a mutual NDA.
      </p>
    </div>
  )
}
